/**
 * F-PRS-117: disable-location appointment-count guard helpers for the
 * practitioner working-hours editor.
 *
 * Flutter parity: `doctor_schedule_tab.dart:469-533`. When editing a
 * working-hours profile, a location that was enabled but is now disabled is
 * "archived"; if it still has upcoming appointments the user is warned before
 * the save commits. Flutter joins the archived location names with `" ,"` and
 * lowercases them in the copy (both quirks preserved verbatim).
 */

export interface GuardLocation {
  id?: string | null;
  name?: string | null;
}

/**
 * Locations present on the original record but absent from the draft — i.e.
 * the ones the user disabled. (React removes disabled locations from
 * `locations[]`; Flutter flags `isArchived` and keeps them — equivalent set.)
 */
export function findDisabledLocations(
  initial: GuardLocation[] | null | undefined,
  draft: GuardLocation[] | null | undefined,
): GuardLocation[] {
  const draftIds = new Set((draft ?? []).map((l) => l.id).filter(Boolean));
  return (initial ?? []).filter((l) => !!l.id && !draftIds.has(l.id));
}

/** Join location names with Flutter's verbatim `" ,"` separator. */
export function joinDisabledLocationNames(locs: GuardLocation[]): string {
  return locs
    .map((l) => l.name ?? '')
    .filter(Boolean)
    .join(' ,');
}

/**
 * Verbatim Flutter warning copy (`doctor_schedule_tab.dart:518-521`),
 * including the original quirks: "If you disable." (stray period), " ,"
 * spacing, and the lowercased location names.
 */
export function disableLocationWarningCopy(
  names: string,
  count: number,
): { title: string; message: string } {
  const lower = names.toLowerCase();
  // parity: verbatim Flutter copy — the "If you disable." stray period and
  // the " ," spacing are INTENTIONAL (match doctor_schedule_tab.dart:518-521).
  // Do NOT "fix" the grammar/spacing; it would break parity with the Flutter
  // string the QA/UAT suites assert against.
  return {
    title: `Do you really want to disable ${lower} visit for this doctor?`,
    message: `There are ${count} future ${lower} appointments scheduled. If you disable. you will not be able to schedule new ${lower} visit , however all existing appointments will remain active`,
  };
}
